import { ChevronDownIcon, ChevronRightIcon } from '@chakra-ui/icons';
import {
  Card,
  CardBody,
  CardHeader,
  CardProps,
  Collapse,
  Flex,
  Icon,
  IconButton,
  Tooltip,
} from '@chakra-ui/react';
import { Eye, EyeOff, Plus } from 'lucide-react';
import { FC, ReactNode, useState } from 'react';

interface CollapsibleSectionProps
  extends Omit<CardProps, 'children' | 'title'> {
  title: ReactNode;
  isExpanded: boolean;
  cardHeaderChildren?: ReactNode;
  onToggle: () => void;
  children: ReactNode;
  headerColor?: string;
  isDebugSection?: boolean;
  showAddButton?: boolean;
  onAddClick?: () => void;
  onShowHideValidationErrors?: (show: boolean) => void;
}

export const CollapsibleSection: FC<CollapsibleSectionProps> = ({
  title,
  isExpanded,
  cardHeaderChildren,
  onToggle,
  children,
  headerColor = 'compliance.card.header.text',
  isDebugSection = false,
  showAddButton = false,
  onAddClick,
  onShowHideValidationErrors,
  ...cardProps
}) => {
  const [showValidationErrors, setShowValidationErrors] = useState(true);
  const debugProps = isDebugSection
    ? {
        bg: 'compliance.debug.bg',
        borderColor: 'compliance.debug.border',
      }
    : {};

  const finalHeaderColor = isDebugSection
    ? 'compliance.debug.text'
    : headerColor;

  return (
    <Card {...debugProps} {...cardProps}>
      <CardHeader p={4}>
        <Flex
          align="center"
          color={finalHeaderColor}
          justifyContent="space-between"
          mb={isExpanded ? 0 : 4}
        >
          <Flex align="center" cursor="pointer" flex={1} onClick={onToggle}>
            {title}
            <Flex ml={2}>
              {isExpanded ? <ChevronDownIcon /> : <ChevronRightIcon />}
            </Flex>
          </Flex>
          {showAddButton && onAddClick && (
            <Tooltip
              bg="compliance.tooltip.bg"
              label="Add Document Issue"
              variant="muted"
            >
              <IconButton
                _hover={{
                  bg: 'compliance.button.secondary.bg',
                }}
                aria-label="Add"
                color="compliance.button.secondary.text"
                fontSize="md"
                height="24px"
                icon={<Icon as={Plus} />}
                p={1}
                size="xs"
                variant="ghost"
                onClick={onAddClick}
              />
            </Tooltip>
          )}
          {onShowHideValidationErrors && (
            <Tooltip
              bg="compliance.tooltip.bg"
              label={
                showValidationErrors
                  ? 'Hide Validation Errors'
                  : 'Show Validation Errors'
              }
              variant="muted"
            >
              <IconButton
                _hover={{
                  bg: 'compliance.button.secondary.bg',
                }}
                aria-label="Show/Hide Validation Errors"
                color="compliance.button.secondary.text"
                fontSize="md"
                height="24px"
                icon={<Icon as={showValidationErrors ? EyeOff : Eye} />}
                p={1}
                size="xs"
                variant="ghost"
                onClick={() => {
                  setShowValidationErrors((prev) => {
                    onShowHideValidationErrors?.(!prev);

                    return !prev;
                  });
                }}
              />
            </Tooltip>
          )}
          {cardHeaderChildren}
        </Flex>
      </CardHeader>
      <Collapse in={isExpanded}>
        <CardBody p={4}>{children}</CardBody>
      </Collapse>
    </Card>
  );
};
